import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { AdminFormFooter } from "@/components/admin/AdminFormFooter";
import { FormActions } from "@/components/FormActions";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { getSettings, saveSiteSettings } from "@/lib/api/settings.functions";
import { testSmtpSettings } from "@/lib/api/newsletter.functions";
import { testEnquiryNotification } from "@/lib/api/contacts.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { defaultFirmContact, parseFirmContact, type FirmContact } from "@/lib/firm-contact";
import { parseBooleanSetting } from "@/lib/site-settings";
import {
  parseSmtpSettings,
  parseEmailSignatureSettings,
  parseEnquiryNotificationSettings,
  parseNotifyEmailsInput,
  formatNotifyEmailsInput,
  defaultSmtpSettings,
  defaultEmailSignatureSettings,
  defaultEnquiryNotificationSettings,
  type SmtpSettings,
  type EmailSignatureSettings,
  type EnquiryNotificationSettings,
} from "@/lib/email/types";
import { useAdminUser } from "@/lib/auth/useAdminUser";
import { canManageSiteConfig } from "@/lib/auth/permissions";

export const Route = createFileRoute("/admin/settings/")({
  loader: () => getSettings(),
  component: AdminSettingsPage,
});

function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4 rounded-lg border p-6">
      <div>
        <h2 className="text-lg font-semibold">{title}</h2>
        {description ? (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function AdminSettingsPage() {
  const settings = Route.useLoaderData();
  const adminUser = useAdminUser();
  const canManageSite = canManageSiteConfig(adminUser.permissions);
  const [gaId, setGaId] = useState(String(settings.google_analytics_id ?? ""));
  const [siteUrl, setSiteUrl] = useState(String(settings.site_url ?? ""));
  const [firmContact, setFirmContact] = useState<FirmContact>(
    parseFirmContact(settings.firm_contact ?? defaultFirmContact),
  );
  const [craftGlobalCss, setCraftGlobalCss] = useState(String(settings.craft_global_css ?? ""));
  const [cardAnimationsEnabled, setCardAnimationsEnabled] = useState(
    parseBooleanSetting(settings.card_animations_enabled, true),
  );
  const [smtpSettings, setSmtpSettings] = useState<SmtpSettings>(
    parseSmtpSettings(settings.smtp_settings ?? defaultSmtpSettings()),
  );
  const [emailSignature, setEmailSignature] = useState<EmailSignatureSettings>(
    parseEmailSignatureSettings(settings.email_signature ?? defaultEmailSignatureSettings()),
  );
  const [enquiryNotifications, setEnquiryNotifications] = useState<EnquiryNotificationSettings>(
    parseEnquiryNotificationSettings(settings.enquiry_notification_settings ?? defaultEnquiryNotificationSettings()),
  );
  const [notifyEmailsInput, setNotifyEmailsInput] = useState(() =>
    formatNotifyEmailsInput(
      parseEnquiryNotificationSettings(settings.enquiry_notification_settings ?? defaultEnquiryNotificationSettings())
        .notifyEmails,
    ),
  );
  const [saving, setSaving] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testingNotification, setTestingNotification] = useState(false);

  function updateAddress(key: keyof FirmContact["address"], value: string | number) {
    setFirmContact((prev) => ({
      ...prev,
      address: { ...prev.address, [key]: value },
    }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const parsedNotifyEmails = parseNotifyEmailsInput(notifyEmailsInput);
      const nextNotificationSettings = {
        ...enquiryNotifications,
        notifyEmails: parsedNotifyEmails,
      };
      await saveSiteSettings({
        data: {
          googleAnalyticsId: gaId,
          siteUrl,
          firmContact,
          cardAnimationsEnabled,
          ...(canManageSite
            ? {
                craftGlobalCss,
                smtpSettings,
                emailSignature,
                enquiryNotificationSettings: nextNotificationSettings,
              }
            : {}),
        },
      });
      setEnquiryNotifications(nextNotificationSettings);
      setNotifyEmailsInput(formatNotifyEmailsInput(parsedNotifyEmails));
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleTestSmtp() {
    setTestingSmtp(true);
    try {
      const parsedNotifyEmails = parseNotifyEmailsInput(notifyEmailsInput);
      await saveSiteSettings({
        data: {
          smtpSettings,
          emailSignature,
          enquiryNotificationSettings: {
            ...enquiryNotifications,
            notifyEmails: parsedNotifyEmails,
          },
        },
      });
      setEnquiryNotifications((prev) => ({ ...prev, notifyEmails: parsedNotifyEmails }));
      setNotifyEmailsInput(formatNotifyEmailsInput(parsedNotifyEmails));
      await testSmtpSettings();
      toast.success("SMTP connection verified");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "SMTP test failed");
    } finally {
      setTestingSmtp(false);
    }
  }

  async function handleTestEnquiryNotification() {
    setTestingNotification(true);
    try {
      const parsedNotifyEmails = parseNotifyEmailsInput(notifyEmailsInput);
      if (parsedNotifyEmails.length === 0) {
        throw new Error("Add at least one notification recipient email.");
      }
      const nextNotificationSettings = {
        enabled: true,
        notifyEmails: parsedNotifyEmails,
      };
      await saveSiteSettings({
        data: {
          smtpSettings,
          emailSignature,
          enquiryNotificationSettings: nextNotificationSettings,
        },
      });
      setEnquiryNotifications(nextNotificationSettings);
      setNotifyEmailsInput(formatNotifyEmailsInput(parsedNotifyEmails));
      await testEnquiryNotification();
      toast.success(`Test enquiry notification sent to ${parsedNotifyEmails.length} recipient(s)`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Test notification failed");
    } finally {
      setTestingNotification(false);
    }
  }

  return (
    <AdminPageShell>
      <AdminPageHeader title="Site settings" />

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="h-auto flex-wrap justify-start gap-1">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="contact">Contact</TabsTrigger>
          {canManageSite ? <TabsTrigger value="email">Email</TabsTrigger> : null}
          {canManageSite ? <TabsTrigger value="craft">Craft CSS</TabsTrigger> : null}
        </TabsList>

        <TabsContent value="general" className="mt-4 space-y-6">
          <SettingsSection title="Analytics & SEO">
            <div className="space-y-2">
              <Label>Google Analytics ID</Label>
              <Input value={gaId} onChange={(e) => setGaId(e.target.value)} placeholder="G-XXXXXXXXXX" />
            </div>
            <div className="space-y-2">
              <Label>Site URL</Label>
              <Input value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} placeholder="https://kslau.com" />
            </div>
          </SettingsSection>

          <SettingsSection
            title="Appearance"
            description="Motion and visual effects on the public site."
          >
            <div className="flex items-center justify-between gap-4 rounded-md border bg-muted/30 px-4 py-3">
              <div className="space-y-0.5">
                <Label htmlFor="card-animations" className="text-base">
                  Card fade-in animation
                </Label>
                <p className="text-sm text-muted-foreground">
                  Cards fade in and slide up slightly as they enter the viewport.
                </p>
              </div>
              <Switch
                id="card-animations"
                checked={cardAnimationsEnabled}
                onCheckedChange={setCardAnimationsEnabled}
              />
            </div>
          </SettingsSection>
        </TabsContent>

        <TabsContent value="contact" className="mt-4">
          <SettingsSection
            title="Contact & office"
            description="Shown on the contact page, footer, chat widget, and site search."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={firmContact.phone}
                  onChange={(e) => setFirmContact((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="+60 4-370 2885"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={firmContact.email}
                  onChange={(e) => setFirmContact((p) => ({ ...p, email: e.target.value }))}
                  placeholder="info@kslau.com.my"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Office hours</Label>
              <Input
                value={firmContact.hours}
                onChange={(e) => setFirmContact((p) => ({ ...p, hours: e.target.value }))}
                placeholder="Monday – Friday, 8.30am - 5.30pm"
              />
            </div>

            <div className="space-y-3">
              <Label>Office address</Label>
              <Input
                value={firmContact.address.line1}
                onChange={(e) => updateAddress("line1", e.target.value)}
                placeholder="Suite 12-A Gurney Tower Office"
              />
              <Input
                value={firmContact.address.line2}
                onChange={(e) => updateAddress("line2", e.target.value)}
                placeholder="18, Persiaran Gurney"
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  value={firmContact.address.line3}
                  onChange={(e) => updateAddress("line3", e.target.value)}
                  placeholder="10250, George Town"
                />
                <Input
                  value={firmContact.address.line4}
                  onChange={(e) => updateAddress("line4", e.target.value)}
                  placeholder="Pulau Pinang, Malaysia"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Map location</Label>
              <p className="text-xs text-muted-foreground">
                Latitude and longitude for the embedded Google Map. Find coordinates in{" "}
                <a
                  href="https://www.google.com/maps"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  Google Maps
                </a>{" "}
                (right-click a place → copy coordinates).
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Latitude</Label>
                  <Input
                    type="number"
                    step="any"
                    value={firmContact.address.lat}
                    onChange={(e) => updateAddress("lat", Number(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Longitude</Label>
                  <Input
                    type="number"
                    step="any"
                    value={firmContact.address.lng}
                    onChange={(e) => updateAddress("lng", Number(e.target.value))}
                  />
                </div>
              </div>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${firmContact.address.lat},${firmContact.address.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-sm text-primary hover:underline"
              >
                Preview location in Google Maps →
              </a>
            </div>
          </SettingsSection>
        </TabsContent>

        <TabsContent value="email" className="mt-4 space-y-6">
          <SettingsSection
            title="Email (SMTP)"
            description={
              <>
                Used for newsletter sends and enquiry notifications. Password can also be set via{" "}
                <code className="text-xs">SMTP_PASSWORD</code> env var.
              </>
            }
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>SMTP host</Label>
                <Input
                  value={smtpSettings.host}
                  onChange={(e) => setSmtpSettings((p) => ({ ...p, host: e.target.value }))}
                  placeholder="smtp.example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Port</Label>
                <Input
                  type="number"
                  value={smtpSettings.port}
                  onChange={(e) => setSmtpSettings((p) => ({ ...p, port: Number(e.target.value) || 587 }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Username</Label>
                <Input
                  value={smtpSettings.user}
                  onChange={(e) => setSmtpSettings((p) => ({ ...p, user: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  value={smtpSettings.password}
                  onChange={(e) => setSmtpSettings((p) => ({ ...p, password: e.target.value }))}
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label>From email</Label>
                <Input
                  type="email"
                  value={smtpSettings.fromEmail}
                  onChange={(e) => setSmtpSettings((p) => ({ ...p, fromEmail: e.target.value }))}
                  placeholder="newsletter@kslau.com.my"
                />
              </div>
              <div className="space-y-2">
                <Label>From name</Label>
                <Input
                  value={smtpSettings.fromName}
                  onChange={(e) => setSmtpSettings((p) => ({ ...p, fromName: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex items-center justify-between gap-4 rounded-md border bg-muted/30 px-4 py-3">
              <div className="space-y-0.5">
                <Label htmlFor="smtp-secure" className="text-base">Use SSL/TLS (port 465)</Label>
                <p className="text-sm text-muted-foreground">Enable for implicit TLS. Leave off for STARTTLS on port 587.</p>
              </div>
              <Switch
                id="smtp-secure"
                checked={smtpSettings.secure}
                onCheckedChange={(checked) => setSmtpSettings((p) => ({ ...p, secure: checked }))}
              />
            </div>
            <FormActions>
              <Button type="button" variant="outline" onClick={() => void handleTestSmtp()} disabled={testingSmtp}>
                {testingSmtp ? "Testing…" : "Save & test connection"}
              </Button>
            </FormActions>
          </SettingsSection>

          <SettingsSection
            title="Enquiry notifications"
            description="Email staff when a new enquiry is submitted from the contact page, enquiry dialog, or floating chat widget. Turn on, add recipients, then click Save at the bottom of this page."
          >
            <div className="flex items-center justify-between gap-4 rounded-md border bg-muted/30 px-4 py-3">
              <div className="space-y-0.5">
                <Label htmlFor="enquiry-notify-enabled" className="text-base">
                  Enable enquiry notifications
                </Label>
                <p className="text-sm text-muted-foreground">
                  Sends an email to the recipients below whenever an enquiry is saved to the admin inbox.
                </p>
              </div>
              <Switch
                id="enquiry-notify-enabled"
                checked={enquiryNotifications.enabled}
                onCheckedChange={(checked) =>
                  setEnquiryNotifications((prev) => ({ ...prev, enabled: checked }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="enquiry-notify-emails">Notification recipients</Label>
              <Input
                id="enquiry-notify-emails"
                type="text"
                value={notifyEmailsInput}
                onChange={(e) => setNotifyEmailsInput(e.target.value)}
                placeholder="info@kslau.com.my, partners@kslau.com.my"
              />
              <p className="text-sm text-muted-foreground">
                Comma-separated email addresses. If left empty, the firm contact email from the Contact tab is used.
              </p>
            </div>
            <FormActions>
              <Button
                type="button"
                variant="outline"
                onClick={() => void handleTestEnquiryNotification()}
                disabled={testingNotification}
              >
                {testingNotification ? "Sending…" : "Send test notification"}
              </Button>
            </FormActions>
          </SettingsSection>

          <SettingsSection
            title="Email signature"
            description="Appended to newsletter emails. Use simple HTML (tables and inline styles work best)."
          >
            <textarea
              className="min-h-[120px] w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
              value={emailSignature.html}
              onChange={(e) => setEmailSignature((p) => ({ ...p, html: e.target.value }))}
              placeholder="<p>Best regards,<br/>KS Lau &amp; Co</p>"
            />
            <div className="flex items-center justify-between gap-4 rounded-md border bg-muted/30 px-4 py-3">
              <div className="space-y-0.5">
                <Label htmlFor="opt-out-link" className="text-base">Include unsubscribe link</Label>
                <p className="text-sm text-muted-foreground">
                  Adds a link to the public opt-out page with reason capture for each recipient.
                </p>
              </div>
              <Switch
                id="opt-out-link"
                checked={emailSignature.includeOptOutLink}
                onCheckedChange={(checked) => setEmailSignature((p) => ({ ...p, includeOptOutLink: checked }))}
              />
            </div>
            {emailSignature.includeOptOutLink ? (
              <div className="space-y-2">
                <Label>Unsubscribe link text</Label>
                <Input
                  value={emailSignature.optOutLinkText}
                  onChange={(e) => setEmailSignature((p) => ({ ...p, optOutLinkText: e.target.value }))}
                />
              </div>
            ) : null}
          </SettingsSection>
        </TabsContent>

        <TabsContent value="craft" className="mt-4">
          <SettingsSection
            title="Craft.js global CSS"
            description="Injected on all Craft-rendered pages alongside per-page custom CSS."
          >
            <textarea
              className="min-h-[240px] w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
              value={craftGlobalCss}
              onChange={(e) => setCraftGlobalCss(e.target.value)}
              placeholder=".craft-hero { padding-top: 2rem; }"
            />
          </SettingsSection>
        </TabsContent>
      </Tabs>

      <AdminFormFooter cancelTo="/admin" onSave={handleSave} saving={saving} saveLabel="Save settings" />
    </AdminPageShell>
  );
}
